<template>
  <div class="allocation-legend">
    <div class="row g-2">
      <!-- PPA Allocation -->
      <div v-if="allocations.ppa.amount > 0" class="col-12 col-sm-6 col-md-3">
        <div class="legend-item d-flex align-items-center">
          <span class="legend-line bg-success me-2"></span>
          <div class="legend-text">
            <div class="legend-label text-muted small">
              {{ $t("production_strategy.reserved_for_ppas") }}
            </div>
            <div class="legend-value fw-semibold">
              {{ formatMwh(allocations.ppa.amount) }}
              {{ $t("common.mwh") }}
            </div>
          </div>
        </div>
      </div>

      <!-- Forward Sales (Combined) -->
      <div
        v-if="allocations.forward.amount > 0"
        class="col-12 col-sm-6 col-md-3"
      >
        <div class="legend-item d-flex align-items-center">
          <span class="legend-line bg-primary me-2"></span>
          <div class="legend-text">
            <div class="legend-label text-muted small">
              {{ $t("production_strategy.hedged_for_forward_sales") }}
            </div>
            <div class="legend-value fw-semibold">
              ~{{ formatMwh(allocations.forward.amount) }}
              {{ $t("common.mwh") }}
            </div>
            <div
              v-if="allocations.forward.forwardIsEstimate"
              class="legend-note text-muted small"
            >
              {{ $t("production_strategy.forward_estimate_note") }}
            </div>
          </div>
        </div>
      </div>

      <!-- Self-managed Allocation -->
      <div
        v-if="allocations.selfManaged.amount > 0"
        class="col-12 col-sm-6 col-md-3"
      >
        <div class="legend-item d-flex align-items-center">
          <span
            class="legend-line me-2"
            :style="{
              backgroundColor:
                PALETTE.semanticOverrides.portfolioType['Self-managed'],
            }"
          ></span>
          <div class="legend-text">
            <div class="legend-label text-muted small">
              {{ $t("production_strategy.self_manage") }}
            </div>
            <div class="legend-value fw-semibold">
              {{ formatMwh(allocations.selfManaged.amount) }}
              {{ $t("common.mwh") }}
            </div>
          </div>
        </div>
      </div>

      <!-- Spot Allocation -->
      <div v-if="allocations.spot.amount > 0" class="col-12 col-sm-6 col-md-3">
        <div class="legend-item d-flex align-items-center">
          <span
            class="legend-line me-2"
            :style="{
              backgroundColor:
                PALETTE.semanticOverrides.portfolioType['Spot Sales'],
            }"
          ></span>
          <div class="legend-text">
            <div class="legend-label text-muted small">
              {{ $t("production_strategy.sell_all_remaining_spot") }}
            </div>
            <div class="legend-value fw-semibold">
              {{ formatMwh(allocations.spot.amount) }}
              {{ $t("common.mwh") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatMwh } from "@/composables/formatUtils";
import { PALETTE } from "@/composables/chartPalette";
import type { AllocationData } from "./useProductionAllocationData";

defineProps<{
  allocations: AllocationData;
}>();
</script>

<style scoped lang="scss">
.allocation-legend {
  .legend-item {
    padding: 0.5rem;
  }

  .legend-line {
    width: 4px;
    height: 32px;
    border-radius: 1px;
    flex-shrink: 0;
  }

  .legend-text {
    flex: 1;
    min-width: 0;
  }

  .legend-label {
    font-size: 0.875rem;
    line-height: 1.2;
  }

  .legend-value {
    font-size: 0.9375rem;
  }

  .legend-note {
    font-size: 0.75rem;
    line-height: 1.2;
  }
}
</style>
